'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { buildDatUrl, resolveSource, type PictureSource } from '@workspace/lib/picture.utils';
import { urlFor } from '@/sanity/lib/image';

export interface ProductImage {
  _key: string;
  alt?: string;
  bynderImage?: {
    alt?: string;
  };
  asset: {
    _ref?: string;
    url: string;
  };
  hotspot?: unknown;
  crop?: unknown;
}

interface UseProductDetailCarouselResult {
  emblaRef: (node: HTMLElement | null) => void;
  emblaApi: UseEmblaCarouselType[1];
  validImages: ProductImage[];
  currentIndex: number;
  isFullscreen: boolean;
  setIsFullscreen: (open: boolean) => void;
  handlePrevious: () => void;
  handleNext: () => void;
  handleThumbnailClick: (index: number) => void;
  isImageLoaded: (index: number) => boolean;
  suppressTransition: boolean;
}

export const useProductDetailCarousel = (
  images: ProductImage[],
  imgSize: { width: number; height: number } = { width: 489, height: 489 },
): UseProductDetailCarouselResult => {
  const { width: renditionWidth, height: renditionHeight } = imgSize;
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]));
  const [suppressTransition, setSuppressTransition] = useState<boolean>(false);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: images?.length > 1,
    align: 'start',
    containScroll: 'trimSnaps',
    duration: 20,
  });

  const validImages = useMemo(() => {
    return (images || []).filter((img) => {
      const hasValidAsset = img.asset?._ref && img.asset._ref !== 'image-' && img.asset._ref.length > 10;
      const hasUrl = img.asset?.url;
      return Boolean(hasValidAsset || hasUrl);
    });
  }, [images]);

  const firstImageKey = useMemo(() => validImages[0]?._key ?? '', [validImages]);

  // Resolve the same rendition URL Picture renders, so preloading actually
  // warms the cache for the displayed image (Bynder DAT transform when
  // available, otherwise the raw asset / Sanity fallback).
  const getDisplayUrl = useCallback(
    (image: ProductImage): string | null => {
      const resolved = resolveSource(image as unknown as PictureSource);
      if (resolved.baseUrl) {
        return resolved.isSvg ? resolved.baseUrl : buildDatUrl(resolved.baseUrl, renditionWidth, renditionHeight);
      }
      return (
        image.asset?.url ||
        (image.asset?._ref && image.asset._ref !== 'image-' ? urlFor(image)?.width(800).quality(90).url() : null)
      );
    },
    [renditionWidth, renditionHeight],
  );

  // Resolve the 2x retina URL for prefetching alongside the 1x rendition.
  const getRetina2xUrl = useCallback(
    (image: ProductImage): string | null => {
      const resolved = resolveSource(image as unknown as PictureSource);
      if (resolved.baseUrl && !resolved.isSvg) {
        return buildDatUrl(resolved.baseUrl, renditionWidth * 2, renditionHeight * 2);
      }
      return null;
    },
    [renditionWidth, renditionHeight],
  );

  const preloadImage = useCallback((imageUrl: string, index: number, retina2xUrl?: string | null) => {
    setLoadedImages((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      // Optimistically mark as loading to avoid race on rapid swipes
      next.add(index);
      return next;
    });

    const img = new Image();
    img.onload = () => {
      setLoadedImages((prev) => new Set(prev).add(index));
    };
    img.src = imageUrl;

    // Also warm the 2x cache for retina displays
    if (retina2xUrl) {
      const img2x = new Image();
      img2x.decoding = 'async';
      img2x.src = retina2xUrl;
    }
  }, []);

  const handlePrevious = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
    } else {
      setCurrentIndex((prev) => (validImages.length === 0 ? 0 : prev === 0 ? validImages.length - 1 : prev - 1));
    }
  }, [emblaApi, validImages.length]);

  const handleNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
    } else {
      setCurrentIndex((prev) => (validImages.length === 0 ? 0 : prev === validImages.length - 1 ? 0 : prev + 1));
    }
  }, [emblaApi, validImages.length]);

  const handleThumbnailClick = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
      else setCurrentIndex(index);
    },
    [emblaApi],
  );

  // Sync index with Embla and preload neighbors
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      const selected = emblaApi.selectedScrollSnap();
      setCurrentIndex(selected);

      const prevIndex = selected === 0 ? validImages.length - 1 : selected - 1;
      const nextIndex = selected === validImages.length - 1 ? 0 : selected + 1;
      const prevImage = validImages[prevIndex];
      const nextImage = validImages[nextIndex];
      if (prevImage) {
        const url = getDisplayUrl(prevImage);
        if (url) preloadImage(url, prevIndex, getRetina2xUrl(prevImage));
      }
      if (nextImage) {
        const url = getDisplayUrl(nextImage);
        if (url) preloadImage(url, nextIndex, getRetina2xUrl(nextImage));
      }
    };

    emblaApi.on('select', onSelect);
    onSelect();
  }, [emblaApi, validImages, preloadImage, getDisplayUrl, getRetina2xUrl]);

  // Preload all images except first
  useEffect(() => {
    validImages.forEach((image, index) => {
      if (index !== 0) {
        const imageUrl = getDisplayUrl(image);
        if (imageUrl) preloadImage(imageUrl, index, getRetina2xUrl(image));
      }
    });
  }, [validImages, preloadImage, getDisplayUrl, getRetina2xUrl]);

  // Reset selection on images change
  useEffect(() => {
    // Suppress fade/transition during variant switch to avoid glitch
    setSuppressTransition(true);
    setCurrentIndex(0);
    setLoadedImages(new Set([0]));
    if (emblaApi) emblaApi.scrollTo(0, false);
    // Release suppression on next frame (twice to ensure styles applied)
    const id = requestAnimationFrame(() => {
      const id2 = requestAnimationFrame(() => setSuppressTransition(false));
      return () => cancelAnimationFrame(id2);
    });
    return () => cancelAnimationFrame(id);
  }, [firstImageKey, validImages.length, emblaApi]);

  // Guard current index when images change
  useEffect(() => {
    if (currentIndex >= validImages.length && validImages.length > 0) {
      setCurrentIndex(0);
      if (emblaApi) emblaApi.scrollTo(0, false);
    }
  }, [currentIndex, validImages.length, emblaApi]);

  // Re-init Embla only when slide count changes
  useEffect(() => {
    if (emblaApi) emblaApi.reInit();
  }, [emblaApi, validImages.length]);

  const isImageLoaded = useCallback(
    (index: number) => loadedImages.has(index) || index === currentIndex,
    [loadedImages, currentIndex],
  );

  return {
    emblaRef,
    emblaApi,
    validImages,
    currentIndex,
    isFullscreen,
    setIsFullscreen,
    handlePrevious,
    handleNext,
    handleThumbnailClick,
    isImageLoaded,
    suppressTransition,
  };
};
